import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Check, ChevronRight, X } from 'lucide-react'
import api from '../../services/api'

const STAPPEN = [
  { id: 'account', label: 'Account aangemaakt', altijd: true },
  { id: 'naam', label: 'Bedrijfsnaam ingevuld', veld: 'naam', link: '/instellingen/profiel' },
  { id: 'btw', label: 'BTW-nummer toevoegen', veld: 'btw_nummer', link: '/instellingen/profiel' },
  { id: 'logo', label: 'Logo uploaden', veld: 'logo_url', link: '/instellingen/profiel' },
  { id: 'klant', label: 'Eerste klant aanmaken', api: 'klanten', link: '/klanten' },
  { id: 'offerte', label: 'Eerste offerte versturen', api: 'offertes', link: '/snelofferte' },
]

export default function OnboardingProgressKaart() {
  const [verborgen, setVerborgen] = useState(false)
  const nav = useNavigate()

  const { data: profiel } = useQuery({
    queryKey: ['tenant-profiel'],
    queryFn: () => api.get('/tenant/profiel').then(r => r.data),
    retry: false,
    staleTime: 60000,
  })

  const { data: klanten } = useQuery({
    queryKey: ['klanten-count'],
    queryFn: () => api.get('/klanten?limit=1').then(r => ({ count: r.data?.length || 0 })),
    retry: false,
    staleTime: 60000,
  })

  const { data: offertes } = useQuery({
    queryKey: ['offertes-count'],
    queryFn: () => api.get('/offertes?limit=1').then(r => ({ count: r.data?.length || 0 })),
    retry: false,
    staleTime: 60000,
  })

  if (verborgen || !profiel) return null

  const p = profiel.profiel || {}
  const vol = profiel.volledigheid || {}
  const pct = vol.pct || 0

  // Bepaal status per stap
  const statusMap = {
    account: true,
    naam: !!p.naam,
    btw: !!p.btw_nummer,
    logo: !!p.logo_url,
    klant: (klanten?.count || 0) > 0,
    offerte: (offertes?.count || 0) > 0,
  }

  const alleKlaar = Object.values(statusMap).every(Boolean)
  if (alleKlaar) return null

  // Vind volgende stap
  let volgendeIdx = STAPPEN.findIndex(s => !statusMap[s.id])

  const kleur = pct >= 80 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#3b82f6'

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6 relative">
      <button onClick={() => setVerborgen(true)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600" title="Verberg">
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-center gap-4 mb-4">
        <div className="relative w-14 h-14 flex-shrink-0">
          <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
            <circle cx="18" cy="18" r="15" fill="none" stroke="#e5e7eb" strokeWidth="3" />
            <circle cx="18" cy="18" r="15" fill="none" stroke={kleur} strokeWidth="3" strokeDasharray={`${pct * 0.942} 100`} strokeLinecap="round" />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-xs font-bold" style={{ color: kleur }}>{pct}%</span>
        </div>
        <div>
          <h3 className="font-bold text-gray-900 dark:text-gray-100">Welkom bij Orion!</h3>
          <p className="text-sm text-gray-500">Uw account is {pct}% compleet</p>
        </div>
      </div>

      <div className="space-y-2">
        {STAPPEN.map((stap, i) => {
          const klaar = statusMap[stap.id]
          const isVolgende = i === volgendeIdx

          return (
            <div key={stap.id} className={`flex items-center gap-3 py-1.5 px-2 rounded-lg ${isVolgende ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
              {klaar ? (
                <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
              ) : isVolgende ? (
                <ChevronRight className="w-5 h-5 text-blue-500 flex-shrink-0" />
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
              )}
              <span className={`text-sm flex-1 ${klaar ? 'text-gray-400 line-through' : isVolgende ? 'text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-500'}`}>
                {stap.label}
              </span>
              {isVolgende && stap.link && (
                <button onClick={() => nav(stap.link)} className="text-xs px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium">
                  Invullen
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
